import {
  FaroErrorBoundary,
  getWebInstrumentations,
  initializeFaro,
  ReactIntegration,
  WebVitalsInstrumentation,
  withFaroProfiler,
  type MetaSession,
} from '@grafana/faro-react';
import {
  getDefaultOTELInstrumentations,
  TracingInstrumentation,
} from '@grafana/faro-web-tracing';
import {
  CompositePropagator,
} from '@opentelemetry/core';
import {
  B3InjectEncoding,
  B3Propagator,
} from '@opentelemetry/propagator-b3';

const botMatcher = '(googlebot/|bot|Googlebot-Mobile|Googlebot-Image|Google favicon|Mediapartners-Google|bingbot|slurp|java|wget|curl|Commons-HttpClient|Python-urllib|libwww|httpunit|nutch|phpcrawl|msnbot|jyxobot|FAST-WebCrawler|FAST Enterprise Crawler|biglotron|teoma|convera|seekbot|gigablast|exabot|ngbot|ia_archiver|GingerCrawler|webmon |httrack|webcrawler|grub.org|UsineNouvelleCrawler|antibot|netresearchserver|speedy|fluffy|bibnum.bnf|findlink|msrbot|panscient|yacybot|AISearchBot|IOI|ips-agent|tagoobot|MJ12bot|dotbot|woriobot|yanga|buzzbot|mlbot|yandexbot|purebot|Linguee Bot|Voyager|CyberPatrol|voilabot|baiduspider|citeseerxbot|spbot|twengabot|postrank|turnitinbot|scribdbot|page2rss|sitebot|linkdex|Adidxbot|blekkobot|ezooms|dotbot|Mail.RU_Bot|discobot|heritrix|findthatfile|europarchive.org|NerdByNature.Bot|sistrix crawler|ahrefsbot|Aboundex|domaincrawler|wbsearchbot|summify|ccbot|edisterbot|seznambot|ec2linkfinder|gslfbot|aihitbot|intelium_bot|facebookexternalhit|yeti|RetrevoPageAnalyzer|lb-spider|sogou|lssbot|careerbot|wotbox|wocbot|ichiro|DuckDuckBot|lssrocketcrawler|drupact|webcompanycrawler|acoonbot|openindexspider|gnam gnam spider|web-archive-net.com.bot|backlinkcrawler|coccoc|integromedb|content crawler spider|toplistbot|seokicks-robot|it2media-domain-crawler|ip-web-crawler.com|siteexplorer.info|elisabot|proximic|changedetection|blexbot|arabot|WeSEE:Search|niki-bot|CrystalSemanticsBot|rogerbot|360Spider|psbot|InterfaxScanBot|Lipperhey SEO Service|CC Metadata Scaper|g00g1e.net|GrapeshotCrawler|urlappendbot|brainobot|fr-crawler|binlar|SimpleCrawler|Livelapbot|Twitterbot|cXensebot|smtbot|bnf.fr_bot|A6-Indexer|ADmantX|Facebot|Twitterbot|OrangeBot|memorybot|AdvBot|MegaIndex|SemanticScholarBot|ltx71|nerdybot|xovibot|BUbiNG|Qwantify|archive.org_bot|Applebot|TweetmemeBot|crawler4j|findxbot|SemrushBot|yoozBot|lipperhey|y!j-asr|Domain Re-Animator Bot|AddThis)';
const botsRegex = new RegExp(botMatcher, 'i');
const originSplitRegex = /^(?<protocol>http[s]?:\/\/)(?<subdomain>.+?)?[\.]?(?:platform\.specia\.ai)?[:]?(?<port>[0-9]+)?(?<path>\/.*)?$/miu;
const subdomainMatch = originSplitRegex.exec(window.location.origin);
const subdomain = subdomainMatch?.groups?.subdomain ?? 'Production';

const ENV_IS_PRODUCTION: boolean = import.meta.env.VITE_ENV_IS_PRODUCTION ?? false;
const FARO_API_KEY: string = import.meta.env.VITE_FARO_API_KEY ?? '';

const SERVICE_NAMESPACE: string = import.meta.env.VITE_FARO_SERVICE_NAMESPACE ?? subdomain;
const SERVICE_NAME: string = import.meta.env.VITE_FARO_SERVICE_NAME ?? 'accountant-portal';
const SERVICE_VERSION: string = import.meta.env.VITE_FARO_SERVICE_VERSION ?? '1.0.0';
const SERVICE_ENVIRONMENT: string = ENV_IS_PRODUCTION ? 'Production' : 'Development';

const SAMPLING_FACTOR = (
  botsRegex.test(navigator.userAgent)
    ? 0
    : import.meta.env.VITE_FARO_SAMPLING_FACTOR
) ?? 1;

export const faro = initializeFaro({
  url: `${window.origin}/faro`,
  apiKey: FARO_API_KEY,
  app: {
    name: SERVICE_NAME,
    version: SERVICE_VERSION,
    namespace: SERVICE_NAMESPACE,
    environment: SERVICE_ENVIRONMENT,
  },
  dedupe: true,
  trackGeolocation: {
    enabled: true,
  },
  batching: {
    enabled: true,
    paused: false,
    itemLimit: 10000,
    sendTimeout: 10000,
  },
  sessionTracking: {
    enabled: true,
    persistent: true,
    samplingRate: SAMPLING_FACTOR,
    onSessionChange: (
      oldSession: MetaSession | null,
      newSession: MetaSession,
    ) => {
      console.debug(`Session ${oldSession == null ? 'created' : 'changed'}`, {
        oldSession,
        newSession,
      });
    },
    session: undefined,
    maxSessionPersistenceTime: 2 * 60 * 60 * 1000, // 2 hours
  },
  instrumentations: [
    ...getWebInstrumentations({
      captureConsole: true,
    }),
    new TracingInstrumentation({
      propagator: new CompositePropagator({
        propagators: [
          new B3Propagator(),
          new B3Propagator({
            injectEncoding: B3InjectEncoding.MULTI_HEADER,
          }),
        ],
      }),
      instrumentationOptions: {
        // Requests to these URLs have tracing headers attached.
        propagateTraceHeaderCorsUrls: [/^https:\/\/(?:.*\.)platform\.specia\.ai(?::[0-9]+)?\/(?:[^r]{1}[^e]*)?$/gi],
      },
      instrumentations: [
        ...getDefaultOTELInstrumentations({
          ignoreUrls: [
            /staging.platform.specia.ai\/realms\/.*/,
            /staging.platform.specia.ai\/keycloak\/.*/,
            /staging.platform.specia.ai\/admin\/.*/,
            /(?:.*?\.)?platform.specia.ai\/faro(?:\/.*?)?/,
          ],
        }),
      ],
    }),
    new WebVitalsInstrumentation(),
    new ReactIntegration(),
  ],
  webVitalsInstrumentation: {
    reportAllChanges: true,
  },
  trackWebVitalsAttribution: true,
  trackResources: true,
  isolate: false,
});

interface AppWrapperProps {
  children: React.ReactNode;
}

function AppErrorWrapper(
  props: AppWrapperProps,
): React.ReactNode {
  const {
    children,
  } = props;
  return (
    <FaroErrorBoundary>
      { children }
    </FaroErrorBoundary>
  );
}

// export const FaroAppWrapper = ENV_IS_PRODUCTION
//   ? AppErrorWrapper
//   : withFaroProfiler(
//       AppErrorWrapper,
//     );
export const FaroAppWrapper = withFaroProfiler(
  AppErrorWrapper,
);
export default FaroAppWrapper;
